import { AgentPromptInput } from "@/components/messages/Omnibox/AgentPromptInput.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Sparkles } from "lucide-react";
import { useState } from "react";

/**
 * A "start a thread about this" affordance for a standalone entity view: a
 * collapsed button that expands into a prompt input seeded with the entity's
 * canonical URN.
 *
 * The agent resolves "this" / "it" from `viewContext.canvasItem`, which only a
 * thread's canvas produces (`useThreadCanvas`). Off that path — a deep-linked
 * communication, the mobile full-page route, the standalone artifact page —
 * there is no composer at all, so an email you land on is a dead end (BIG-6336).
 * Stating the URN outright gives those surfaces the same deixis the canvas gives
 * a thread, with no server change: `viewContext` is already a per-message field.
 *
 * Page-scoped enrichments are deliberately excluded. This composer is bound to
 * one entity, and on a surface where the calendar happens to be mounted (the
 * canvas source modal) the ambient view week would otherwise ride along and read
 * to the agent as relevant to the reply.
 */
export const EntityAgentComposer = ({
  urn,
  label,
  placeholder,
  statsigName,
}: {
  /** Canonical URN of the entity in view, e.g. `urn:bb:source:123`. */
  urn: string;
  /** Collapsed-state button text. */
  label: string;
  /** Expanded-state textarea placeholder. */
  placeholder: string;
  /** Analytics name for the collapsed button. */
  statsigName: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!isOpen) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-fit text-muted-foreground"
        onClick={() => setIsOpen(true)}
        data-statsig-name={statsigName}
      >
        <Sparkles className="size-3.5 shrink-0" />
        {label}
      </Button>
    );
  }

  return (
    <AgentPromptInput
      placeholder={placeholder}
      viewContext={{ canvasItem: urn }}
      hostsPageEnrichments={false}
      alwaysOpenNewThread
      onEscape={() => setIsOpen(false)}
      onSubmitted={() => setIsOpen(false)}
    />
  );
};
